const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const LAYOUTS = {
  strip: { label: "Slim strip", hint: "Subtle · ~1200×120", kind: "image" },
  banner: { label: "Banner", hint: "Standard · ~1200×400", kind: "image" },
  billboard: { label: "Billboard", hint: "Biggest · ~1200×800", kind: "image" },
  square: { label: "Square tile", hint: "1:1 · logos & products · ~800×800", kind: "image" },
  poster: { label: "Poster", hint: "4:5 tall · flyers · ~800×1000", kind: "image" },
  card: { label: "Headline card", hint: "Media left · headline + CTA right", kind: "text" },
  overlay: { label: "Overlay", hint: "Full-width media · headline + CTA on the bottom edge", kind: "text" },
  twoup: { label: "Two-up", hint: "Two images side by side · one click-through", kind: "twoup" },
};

const H = { strip: "h-14 sm:h-16", banner: "h-40 sm:h-56", billboard: "h-64 sm:h-96" };

export const resolveAdSrc = (src) => (!src ? "" : src.startsWith("http") || src.startsWith("data:") || src.startsWith("blob:") ? src : `${BACKEND}${src}`);

const fitOf = (ad, positioned) => (positioned ? ad.fit || "contain" : "contain");

function bg(src, ad, positioned = true) {
  const fit = fitOf(ad, positioned);
  return {
    backgroundImage: `url(${resolveAdSrc(src)})`,
    backgroundSize: fit === "custom" ? `${ad.zoom || 100}% auto` : fit === "cover" ? "cover" : "contain",
    backgroundPosition: positioned ? `${ad.pos_x ?? 50}% ${ad.pos_y ?? 50}%` : "50% 50%",
    backgroundRepeat: "no-repeat",
  };
}

const GLOW = { textShadow: "0 0 10px rgba(255,255,255,0.65), 0 0 22px rgba(255,255,255,0.35)" };

function Copy({ ad, align = "left" }) {
  return (
    <div className={`min-w-0 ${align === "left" ? "" : "text-right"}`}>
      {ad.advertiser && <div className="text-[9px] uppercase tracking-[0.3em] text-pb-cyan font-bold truncate" data-testid="ad-copy-advertiser">{ad.advertiser}</div>}
      <div className="font-display font-black uppercase text-white text-lg sm:text-2xl leading-tight" style={GLOW} data-testid="ad-copy-headline">{ad.headline || "Your headline"}</div>
      {ad.subtext && <div className="text-xs sm:text-sm text-zinc-300 mt-1 line-clamp-2" data-testid="ad-copy-subtext">{ad.subtext}</div>}
    </div>
  );
}

function Cta({ ad }) {
  return (
    <span className="inline-flex items-center shrink-0 bg-pb-red text-black font-black uppercase tracking-widest text-[10px] sm:text-xs px-4 py-2.5 shadow-[0_0_14px_rgba(163,230,53,0.55)]" data-testid="ad-cta">
      {ad.cta_label || "Learn more"}
    </span>
  );
}

/** Main media slot: video (autoplay, muted, loop) when the ad has one, else the positioned image. */
function Media({ ad, cls, imageProps = {}, children }) {
  const base = { role: "img", "aria-label": ad.title, ...imageProps, className: `${cls} bg-black ${imageProps.className || ""}` };
  if (ad.video) {
    const fit = fitOf(ad, true);
    return (
      <div {...base} className={`${base.className} relative overflow-hidden`} data-testid="ad-video-wrap">
        <video src={resolveAdSrc(ad.video)} poster={ad.image ? resolveAdSrc(ad.image) : undefined} autoPlay muted loop playsInline preload="metadata"
          className={`w-full h-full pointer-events-none ${fit === "cover" ? "object-cover" : "object-contain"}`}
          style={{ objectPosition: `${ad.pos_x ?? 50}% ${ad.pos_y ?? 50}%`, transform: fit === "custom" ? `scale(${(ad.zoom || 100) / 100})` : undefined }}
          data-testid="ad-video" />
        {children}
      </div>
    );
  }
  return <div {...base} style={bg(ad.image, ad)}>{children}</div>;
}

/** Renders any ad layout. `imageProps` (ref + pointer handlers) is spread onto the main media box so the panel can drag-position it. */
export default function AdCreative({ ad, imageProps = {}, className = "" }) {
  const size = LAYOUTS[ad.size] ? ad.size : "banner";

  if (size === "square") return <div className={`flex justify-center bg-black/40 ${className}`}><Media ad={ad} cls="w-full max-w-[320px] aspect-square" imageProps={imageProps} /></div>;
  if (size === "poster") return <div className={`flex justify-center bg-black/40 ${className}`}><Media ad={ad} cls="w-full max-w-[360px] aspect-[4/5]" imageProps={imageProps} /></div>;
  if (size === "card") {
    return (
      <div className={`flex flex-col sm:flex-row bg-black ${className}`}>
        <Media ad={ad} cls="w-full sm:w-2/5 h-40 sm:h-56 shrink-0" imageProps={imageProps} />
        <div className="flex-1 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-zinc-950 border-t sm:border-t-0 sm:border-l border-white/10">
          <Copy ad={ad} />
          <Cta ad={ad} />
        </div>
      </div>
    );
  }
  if (size === "overlay") {
    return (
      <Media ad={ad} cls={`relative w-full h-64 sm:h-96 ${className}`} imageProps={imageProps}>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent p-5 pt-10 flex items-end justify-between gap-4 pointer-events-none">
          <Copy ad={ad} />
          <Cta ad={ad} />
        </div>
      </Media>
    );
  }
  if (size === "twoup") {
    return (
      <div className={`grid grid-cols-2 gap-px bg-white/10 ${className}`}>
        <Media ad={ad} cls="h-40 sm:h-56" imageProps={imageProps} />
        <div role="img" aria-label={ad.title} className="h-40 sm:h-56 bg-black" style={ad.image2 ? bg(ad.image2, ad, false) : undefined} data-testid="ad-image2">
          {!ad.image2 && <div className="h-full flex items-center justify-center text-[9px] uppercase tracking-widest text-zinc-600">2nd image</div>}
        </div>
      </div>
    );
  }
  return <Media ad={ad} cls={`w-full ${H[size]} ${className}`} imageProps={imageProps} />;
}
